/**
 * SearchInput — Debounced search for conversation sidebar.
 *
 * Layer: C (UX/CLI)
 * Spec: Uppdrag C §2.4
 *
 * @decision 200ms debounce per spec. Client-side only (no server search for MVP).
 * @decision Inline SVG search icon — no additional icon dependency.
 * @decision Clear button appears when input has value.
 */

import { useState, useEffect, useRef, useCallback } from 'react';

// ─── Types ────────────────────────────────────────────────────────────

export interface SearchInputProps {
  onSearch: (query: string) => void;
  placeholder?: string;
}

// ─── Component ────────────────────────────────────────────────────────

export function SearchInput({
  onSearch,
  placeholder = 'Sök konversationer...',
}: SearchInputProps) {
  const [value, setValue] = useState('');
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Debounced callback
  const debouncedSearch = useCallback(
    (query: string) => {
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => {
        onSearch(query.trim().toLowerCase());
      }, 200);
    },
    [onSearch],
  );

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setValue(newValue);
    debouncedSearch(newValue);
  };

  const handleClear = () => {
    setValue('');
    onSearch('');
    if (timerRef.current) clearTimeout(timerRef.current);
  };

  return (
    <div className="relative px-3 mb-2">
      {/* Search icon */}
      <svg
        className="absolute left-5 top-1/2 -translate-y-1/2 text-text-secondary/50 pointer-events-none"
        xmlns="http://www.w3.org/2000/svg"
        width="14"
        height="14"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <circle cx="11" cy="11" r="8" />
        <path d="m21 21-4.3-4.3" />
      </svg>

      <input
        type="text"
        value={value}
        onChange={handleChange}
        placeholder={placeholder}
        className={[
          'w-full pl-8 pr-8 py-1.5 rounded-md text-sm',
          'bg-bg-tertiary/70 border border-border/50',
          'text-text-primary placeholder:text-text-secondary/40',
          'focus:outline-none focus:border-accent/50 focus:ring-1 focus:ring-accent/30',
          'transition-colors duration-150',
        ].join(' ')}
        aria-label="Sök konversationer"
      />

      {/* Clear button */}
      {value.length > 0 && (
        <button
          type="button"
          onClick={handleClear}
          className={[
            'absolute right-5 top-1/2 -translate-y-1/2',
            'text-text-secondary/50 hover:text-text-primary transition-colors',
            'focus:outline-none',
          ].join(' ')}
          aria-label="Rensa sökning"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M18 6 6 18" />
            <path d="m6 6 12 12" />
          </svg>
        </button>
      )}
    </div>
  );
}
